import { PROMOTION_RANK, type MoveRisk, type ShieldMap, indexToSquare, squareToIndex } from '@strategy4kids/game-engine';
import { useMemo } from 'react';
import type { SquareTarget } from '../state/game-store.js';

const ORDER: string[] = (() => {
  const out: string[] = [];
  for (let r = 0; r < 8; r++) {
    for (let f = 0; f < 8; f++) {
      out.push(`${String.fromCharCode(97 + f)}${8 - r}`);
    }
  }
  return out;
})();

interface SquareBadge {
  /** Count of friendly defenders, or 0. */
  shields: number;
  /** Count of enemy attackers, or 0. */
  threats: number;
}

export function SquareOverlay({
  selected,
  targets,
  onTap,
  ready = true,
  predictedReply = null,
  opponentArrow,
  shields = null,
  reveal = null,
  revealShields = null,
  promotionRow = null,
  movable = null,
  paths = null,
  unsafe = null,
  strongTargets = false,
  transform,
}: {
  selected: string | null;
  targets: readonly SquareTarget[];
  onTap: (square: string) => void;
  /**
   * Whether the 3D board underneath has come up yet. The screens determine it
   * with `useBoardReady` + `<Canvas onCreated>`. It defaults to true because a
   * caller that cannot tell should not be made to lie about it.
   *
   * This prop is purely a TELEMETRY signal now — it drives `data-board-ready`
   * so audit flows can wait for the first paint before screenshotting. It does
   * NOT gate input. The overlay is always interactive, including during the
   * ~130 ms–2 s window before the 3D canvas paints.
   *
   * Why: gating input during that window made the game literally unplayable
   * for any tap that landed before the canvas reported — kids tap fast, and a
   * disabled square with no visible feedback reads as "the game is broken".
   * The cost of ungating is a brief selection ring on an empty surface, which
   * disappears as soon as the board paints and is much less harmful than a
   * dead tap. Functional > cosmetic.
   */
  ready?: boolean;
  /** Look-ahead chain (set on `tap()`, cleared on commit / undo /
   *  deselect). Mirrored as a slash-separated UCI string into the
   *  `data-testid="predicted-reply-state"` testid so audit flows can
   *  assert on the hint — the 3D meshes themselves carry no DOM
   *  testids. Empty string when the engine returned no PV or the
   *  look-ahead is disabled. */
  predictedReply?: ReadonlyArray<{ from: string; to: string }> | null;
  /** The opponent's last move, mirrored to the DOM so audit flows can
   *  assert on it. The 3D arrow itself can't carry a `data-testid`; the
   *  audit asserts on this invisible span. */
  opponentArrow?: { from: string; to: string } | null;
  /**
   * Defence/attack summary for the viewer's own pieces, from `buildShieldMap`
   * (already scoped to one colour). Drives the corner count badges.
   *
   * The count lives in the DOM rather than the 3D layer on purpose: a corner
   * badge cannot collide with the centred 3D rings, whereas a DOM *fill* would
   * fight `bg-mint/40` and `bg-red-400/40`, which are already the two
   * strongest colours on the board. The rings themselves stay in 3D.
   */
  shields?: ShieldMap | null;
  /** Square whose shield / threat detail is expanded. */
  reveal?: string | null;
  /** Map used to resolve `reveal`. Defaults to `shields`; the screen passes
   *  the *opponent's* map when the revealed square holds an enemy piece, so
   *  "what is protecting that man?" is answerable. Without it, tapping an
   *  enemy piece would always report `none` — a promise the feature does not
   *  keep. */
  revealShields?: ShieldMap | null;
  /** Checkers only: the colour whose crown row carries the promotion band.
   *  Null (and chess) forgoes the `promotion-row-state` mirror entirely —
   *  absent, not empty, so a `[data-testid="promotion-row-state"]` query
   *  selects only games where the feature exists. */
  promotionRow?: 'w' | 'b' | null;
  /** Movable-piece squares (assistance option); mirrored as `data-movable`. */
  movable?: ReadonlyArray<string> | null;
  /** Squares the selected piece passes over; tinted softly (`data-path`). */
  paths?: ReadonlyArray<string> | null;
  /** Destinations that are attacked and undefended; get a warning symbol
   *  (`data-unsafe`). The 3D layer carries no equivalent, same as shield badges. */
  unsafe?: ReadonlyMap<string, MoveRisk> | null;
  /** Draw destination squares stronger than the default (move-path option). */
  strongTargets?: boolean;
  /** CSS `matrix3d` from the board's `onOverlayTransform`: warps this flat grid
   *  onto the perspective board so each cell sits over its 3D square. Undefined
   *  (before the canvas has reported) leaves the grid flat. */
  transform?: string;
}) {
  const pathSet = useMemo(() => new Set(paths ?? []), [paths]);
  const movableSet = useMemo(() => new Set(movable ?? []), [movable]);
  const targetSet = useMemo(() => new Map(targets.map((t) => [t.square, t.capture])), [targets]);
  const replyText = useMemo(
    () =>
      predictedReply && predictedReply.length > 0
        ? predictedReply.map((p) => `${p.from}${p.to}`).join('/')
        : '',
    [predictedReply],
  );
  const arrowText = opponentArrow ? `${opponentArrow.from}-${opponentArrow.to}` : 'none';

  // Always-on per-square badge state, from the *viewer's* map only. The
  // shield map is keyed by board index, so this is where the index →
  // algebraic conversion happens, once, for the DOM layer.
  //
  // Deliberately excludes the reveal. `shield-state` is the audit mirror of
  // the ambient readout, and it has to stay a function of the position alone
  // — otherwise a kid's exploratory tap would change a value the audit flow
  // treats as ground truth, and two positions could no longer be compared with
  // one equality assertion.
  const badges = useMemo(() => {
    const out = new Map<string, SquareBadge>();
    if (shields) {
      for (const [index, list] of shields.defended) {
        const sq = indexToSquare(index);
        const b = out.get(sq) ?? { shields: 0, threats: 0 };
        b.shields = list.length;
        out.set(sq, b);
      }
      for (const [index, list] of shields.threatened) {
        const sq = indexToSquare(index);
        const b = out.get(sq) ?? { shields: 0, threats: 0 };
        b.threats = list.length;
        out.set(sq, b);
      }
    }
    return out;
  }, [shields]);

  /**
   * Squares naming a defender or attacker for the current reveal, plus the
   * revealed square's own counts. Resolved against `revealShields`, which is
   * the *opponent's* map when the revealed square holds an enemy piece — that
   * is what makes "what is protecting that man?" answerable, since the
   * viewer's map never records enemy pieces as subjects.
   */
  const detail = useMemo(() => {
    const map = revealShields ?? shields;
    if (!map || !reveal) return null;
    const index = squareToIndex(reveal);
    const defended = map.defended.get(index) ?? [];
    const threatened = map.threatened.get(index) ?? [];
    return {
      square: reveal,
      shields: defended.length,
      threats: threatened.length,
      sources: new Set([...defended, ...threatened].map(indexToSquare)),
    };
  }, [shields, revealShields, reveal]);

  /**
   * Audit mirror for the always-on markers, in the style of the two existing
   * `sr-only` spans: `square:shields:threats`, pipe-separated, walked in
   * `ORDER` so the string is byte-stable for E2E equality assertions.
   */
  const shieldStateText = useMemo(
    () =>
      ORDER.map((square) => {
        const b = badges.get(square);
        if (!b || (b.shields === 0 && b.threats === 0)) return null;
        return `${square}:${b.shields}:${b.threats}`;
      })
        .filter((s): s is string => s != null)
        .join('|'),
    [badges],
  );

  /** Audit mirror for the expanded square and who is shielding it. */
  const revealText = useMemo(() => {
    if (!detail) return '';
    const names: string[] = [];
    if (detail.shields > 0) names.push(`shielded-by-${detail.shields}`);
    if (detail.threats > 0) names.push(`attacked-by-${detail.threats}`);
    return `${detail.square}:${names.length > 0 ? names.join('+') : 'none'}`;
  }, [detail]);

  /**
   * Audit mirror for the promotion band: `crown-row:<rank>` in the byte-stable
   * token style of the spans above. The rank, not a colour letter, so the
   * assertion reads as the fact on the board ("crown on rank 8") rather than
   * requiring the reader to know the colour convention.
   */
  const promotionRowText =
    promotionRow === 'w'
      ? `crown-row:${PROMOTION_RANK.w}`
      : promotionRow === 'b'
        ? `crown-row:${PROMOTION_RANK.b}`
        : null;

  /** Screen-reader label: the 3D rings are invisible to assistive tech, so
   *  this DOM layer is the only accessible surface for shield state. */
  const labelFor = (square: string): string => {
    const b = badges.get(square);
    const parts = [`square ${square}`];
    if (b) {
      if (b.shields > 0) parts.push(`${b.shields} shield${b.shields === 1 ? '' : 's'}`);
      if (b.threats > 0) parts.push(`attacked by ${b.threats}`);
    }
    return parts.join(', ');
  };
  return (
    <div
      className="absolute inset-0 grid grid-cols-8 grid-rows-8"
      data-testid="square-overlay"
      style={transform ? { transform, transformOrigin: '0 0' } : undefined}
      data-appdom-id="square-overlay"
      data-appdom-type="Grid"
      // Not an app-dom attribute, deliberately. `data-appdom-*` answers "what
      // is this and where"; this answers "has the board painted yet", which is
      // state the audit runner waits on. Keeping it out of the family stops it
      // being read as identity by anything walking INSTRUMENTED_SELECTOR.
      data-board-ready={ready ? 'true' : 'false'}
    >
      {/* Invisible DOM mirror of the opponent arrow's from-to squares. The 3D
          arrow can't carry a `data-testid`, so audit flows assert on this
          span instead. `sr-only` keeps it visually invisible while still
          readable to the audit runner. */}
      <span data-testid="opponent-arrow-state" className="sr-only">
        {arrowText}
      </span>
      {ORDER.map((square) => {
        const isTarget = targetSet.has(square);
        const isCapture = targetSet.get(square) === true;
        const isSelected = selected === square;
        const badge = badges.get(square);
        const shields = badge?.shields ?? 0;
        const threats = badge?.threats ?? 0;
        return (
          <button
            key={square}
            type="button"
            aria-label={labelFor(square)}
            onClick={() => onTap(square)}
            data-square={square}
            data-testid={`square-${square}`}
            // Absent rather than 0 when there is nothing to report, so a
            // `[data-shields]` query selects only the squares that actually
            // carry a shield. `data-shields="0"` on 50 squares would make
            // every audit assertion a "not(0)" dance.
            {...(shields > 0 ? { 'data-shields': shields } : null)}
            {...(threats > 0 ? { 'data-threats': threats } : null)}
            {...(pathSet.has(square) ? { 'data-path': 'true' } : null)}
            {...(unsafe?.has(square) ? { 'data-unsafe': unsafe.get(square) } : null)}
            {...(movableSet.has(square) ? { 'data-movable': 'true' } : null)}
            {...(detail?.sources.has(square) ? { 'data-shield-source': 'true' } : null)}
            className={`relative m-0.5 rounded-md transition-colors ${
              isSelected
                ? 'bg-sun/70 ring-2 ring-sun'
                : isCapture
                  ? strongTargets
                    ? 'bg-red-400/60 ring-2 ring-red-400'
                    : 'bg-red-400/40'
                  : isTarget
                    ? strongTargets
                      ? 'bg-mint/60 ring-2 ring-mint'
                      : 'bg-mint/40'
                    : pathSet.has(square)
                      ? 'bg-mint/15'
                      : 'hover:bg-white/10'
            }`}
          >
            {/* Corner badges. `pointer-events-none` is load-bearing: these sit
                inside the only input surface on the board, so a badge that
                swallowed a click would make that square untappable. The dots
                are top-left (grape, defends) and top-right (berry, attacked)
                so a piece that is both covered and hanging shows both at
                once. Counts are hidden from assistive tech because
                `aria-label` already carries them in a sentence. */}
            {shields > 0 && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-0.5 top-0.5 flex h-3 min-w-3 items-center justify-center rounded-full bg-grape px-1 text-[0.5625rem] font-bold leading-none text-cream"
              >
                {shields}
              </span>
            )}
            {unsafe?.has(square) && (
              <span
                role="img"
                aria-label={
                  unsafe.get(square) === 'lost'
                    ? 'warning: this piece would be lost'
                    : 'warning: you would lose material in the trade'
                }
                data-testid={`unsafe-${square}`}
                className={`pointer-events-none absolute bottom-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[0.6875rem] font-black leading-none ${
                  unsafe.get(square) === 'lost'
                    ? 'bg-amber-400 text-ink'
                    : 'border-2 border-amber-400 bg-cream text-ink'
                }`}
              >
                {unsafe.get(square) === 'lost' ? '!' : '?'}
              </span>
            )}
            {threats > 0 && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-0.5 top-0.5 flex h-3 min-w-3 items-center justify-center rounded-full bg-berry px-1 text-[0.5625rem] font-bold leading-none text-cream"
              >
                {threats}
              </span>
            )}
          </button>
        );
      })}
      {/* Invisible audit mirror of the always-on shield markers, in the same
          style as the two spans above. Pipe-separated `sq:shields:threats`,
          walked in ORDER so the string is byte-stable across renders and two
          positions can be compared with a single equality assertion. */}
      <span
        data-testid="shield-state"
        data-appdom-id="shield-state"
        data-appdom-type="Text"
        className="sr-only"
      >
        {shieldStateText}
      </span>
      {/* Which piece is shielding the revealed square, if any. `aria-live` so
          a screen reader announces the answer when the tap happens — the 3D
          source dots are invisible to assistive tech, which makes this the
          only way the reveal reaches that audience. */}
      <span
        data-testid="shield-reveal-state"
        data-appdom-id="shield-reveal-state"
        data-appdom-type="Text"
        className="sr-only"
        aria-live="polite"
      >
        {revealText}
      </span>
      {/* Invisible audit mirror for the look-ahead chain. Visually hidden
          (sr-only) so it's screen-reader-invisible too — the chain is
          read aloud only via the visible rings on the board. */}
      <span
        data-testid="predicted-reply-state"
        data-appdom-id="predicted-reply-state"
        data-appdom-type="Text"
        className="sr-only"
      >
        {replyText}
      </span>
      {/* Invisible audit mirror of the promotion band — checkers games only.
          Conditional render (not empty string) so the testid's very presence
          answers "is this a game with a crown row", and chess assertions can
          select its absence outright. The band itself is a 3D mesh with no
          DOM testid, same as every other overlay. */}
      {promotionRowText && (
        <span
          data-testid="promotion-row-state"
          data-appdom-id="promotion-row-state"
          data-appdom-type="Text"
          className="sr-only"
        >
          {promotionRowText}
        </span>
      )}
    </div>
  );
}
